import { ApiError } from '../../interop/problem-details.js';
import { AuthenticateModalLit } from '../ui/loginscreen/authenticate-modal-lit.js';
import { authenticationOptions } from './api-injector.js';
import type { ResultTenantLogin } from './current-user.js';
import { showServiceRestartingNotification } from './service-restart-handler.js';
import { sharedExecute } from '../common/helpers/callbacks';
import type { EventNotify } from '../ui/events.js';
import type { ValidationError } from './validation-error.js';
import type {
  IApiErrorHandler,
  IAuthenticationHandler,
  IServiceRestartHandler
} from './api-communications-handlers.js';

export type ErrorResponseHandler = (error: ApiError | ValidationError[]) => Promise<void>;

/**
 * Dealer authentication handler that shows the AuthenticateModalLit for re-authentication.
 */
export class DealerAuthenticationHandler implements IAuthenticationHandler {
  private redirectToLoginPage: EventNotify;
  private _authenticate = sharedExecute(async () => {
    const modal: AuthenticateModalLit = new AuthenticateModalLit(authenticationOptions());
    await modal.showModal();
    return modal.result;
  });

  constructor(redirectToLoginPage: EventNotify) {
    this.redirectToLoginPage = redirectToLoginPage;
  }

  async authenticate(): Promise<ResultTenantLogin | null> {
    return this._authenticate();
  }

  onAuthenticationFailed(): void {
    this.redirectToLoginPage();
  }
}

/**
 * Dealer error handler that uses the provided response handler callback.
 */
export class DealerErrorHandler implements IApiErrorHandler {
  private responseHandler?: ErrorResponseHandler;

  constructor(responseHandler?: ErrorResponseHandler) {
    this.responseHandler = responseHandler;
  }

  async handleError(error: ApiError): Promise<void> {
    if (this.responseHandler) {
      await this.responseHandler(error);
    }
  }

  async handleValidationErrors(errors: ValidationError[]): Promise<void> {
    if (this.responseHandler) {
      await this.responseHandler(errors);
    }
  }
}

/**
 * Dealer service restart handler that shows toast notifications.
 */
export class DealerServiceRestartHandler implements IServiceRestartHandler {
  onServiceRestarting(): void {
    showServiceRestartingNotification();
  }

  onServiceRestartFailed(): void {
    // The showServiceRestartingNotification already handles this case
    // by showing a persistent notification
    console.error('Service restart failed after all retries');
  }
}

